/**
 * DevLoginPage — temporary login page for dev/test environments.
 * Supports direct identifier login (email / username-like employee ID).
 * Only rendered when devLoginEnabled === true and user is not authenticated.
 */
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function DevLoginPage() {
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [identifier, setIdentifier] = useState('');

  const handleLogin = async (value) => {
    const normalized = (value || '').trim();
    if (!normalized) {
      setError('Username or email is required');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await login(normalized);
    } catch (err) {
      setError(err?.message || err?.error || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    await handleLogin(identifier);
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <h1 style={styles.title}>DaVinci Portal</h1>
        <p style={styles.subtitle}>Development Login</p>

        <form style={styles.form} onSubmit={onSubmit}>
          <label htmlFor="dev-login-identifier" style={styles.inputLabel}>
            Username / Email
          </label>
          <input
            id="dev-login-identifier"
            type="text"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="Enter username or email"
            disabled={busy}
            style={styles.input}
          />
          <button type="submit" disabled={busy} style={styles.btnPrimary}>
            {busy ? 'Signing in...' : 'Continue'}
          </button>
        </form>

        {error && <p style={styles.error}>{error}</p>}

        <p style={styles.note}>
          This page is only available in dev/test environments.
        </p>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    width: '380px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  },
  logo: { fontSize: '48px', marginBottom: '8px' },
  title: { color: '#e6edf3', margin: '0 0 4px', fontSize: '24px' },
  subtitle: { color: '#8b949e', margin: '0 0 24px', fontSize: '14px' },
  form: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' },
  inputLabel: {
    textAlign: 'left',
    color: '#c9d1d9',
    fontSize: '13px',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    fontSize: '14px',
    color: '#e6edf3',
    background: '#0d1117',
    border: '1px solid #30363d',
    borderRadius: '8px',
    outline: 'none',
  },
  btnPrimary: {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    background: '#1f6feb',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  error: { color: '#f85149', marginTop: '16px', fontSize: '13px' },
  note: { color: '#484f58', marginTop: '24px', fontSize: '12px' },
};

